import {
  User,
  Calendar,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Award,
  BarChart3,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTranslations } from '@/hooks/use-translations';
import { CERTIFICATION_CONFIG } from '@/modules/certification/constants';

interface UserStats {
  username: string;
  caseSelectorVisits: number;
  caseSelectorAttempts: number;
  completions: number;
  startDate: string | null;
  nps: number | null;
  scores: string;
  status: 'completed' | 'failed' | 'pending';
  nbrAttempts: number;
  hasAccessed: boolean;
  hasCompleted: boolean;
}

interface UserDetailsModalProps {
  user: UserStats | null;
  isOpen: boolean;
  onClose: () => void;
}

export function UserDetailsModal({ user, isOpen, onClose }: UserDetailsModalProps) {
  const { t } = useTranslations('common');

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] max-w-[95%] rounded-md p-0 px-4 md:mx-auto md:max-w-2xl"
        hideDefaultClose
      >
        <ScrollArea className="max-h-[90vh]">
          <div className="px-0 py-4 md:p-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="flex items-center gap-2 font-heading text-2xl text-livelinx-purple200">
                <User className="h-6 w-6" />
                {user.username}
              </DialogTitle>
              <DialogClose className="absolute right-0 top-4 rounded-sm opacity-70 outline-none transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-5 w-5" />
                <span className="sr-only">{t('buttons.close')}</span>
              </DialogClose>
            </DialogHeader>

            {/* Certification Status */}
            <div className="mb-6">
              <h3 className="mb-2 flex items-center gap-2 font-body text-sm text-livelinx-grey200">
                <Award className="h-4 w-4" />
                {t('stats.certificationStatus')}
              </h3>
              <div className="flex gap-2">
                {user.status === 'completed' && (
                  <Badge className="bg-livelinx-green text-white hover:bg-livelinx-green">
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                    {t('stats.passed')}
                  </Badge>
                )}
                {user.status === 'failed' && (
                  <Badge className="bg-livelinx-red text-white hover:bg-livelinx-red">
                    <XCircle className="mr-1 h-4 w-4" />
                    {t('stats.failed')}
                  </Badge>
                )}
                {user.status === 'pending' && <Badge variant="outline">{t('stats.pending')}</Badge>}
              </div>
            </div>

            {/* Activity Overview */}
            <div className="mb-6">
              <h3 className="mb-3 flex items-center gap-2 font-body text-sm text-livelinx-grey200">
                <BarChart3 className="h-4 w-4" />
                {t('stats.activity')}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-card p-3">
                  <p className="text-xs text-livelinx-grey200">{t('stats.visits')}</p>
                  <p className="font-heading text-2xl text-livelinx-purple200">
                    {user.caseSelectorVisits}
                  </p>
                </div>
                <div className="rounded-lg bg-card p-3">
                  <p className="text-xs text-livelinx-grey200">{t('stats.attempts')}</p>
                  <p className="font-heading text-2xl text-livelinx-purple200">
                    {user.caseSelectorAttempts}
                  </p>
                </div>
                <div className="rounded-lg bg-card p-3">
                  <p className="text-xs text-livelinx-grey200">{t('stats.completions')}</p>
                  <p className="font-heading text-2xl text-livelinx-purple200">
                    {user.completions}
                  </p>
                </div>
                <div className="rounded-lg bg-card p-3">
                  <p className="text-xs text-livelinx-grey200">{t('stats.testAttempts')}</p>
                  <p className="font-heading text-2xl text-livelinx-purple200">
                    {user.nbrAttempts}/{CERTIFICATION_CONFIG.MAX_ATTEMPTS}
                  </p>
                </div>
              </div>
            </div>

            {/* Certification Details */}
            {user.scores && (
              <div className="mb-6">
                <h3 className="mb-2 flex items-center gap-2 font-body text-sm text-livelinx-grey200">
                  <TrendingUp className="h-4 w-4" />
                  {t('stats.testScores')}
                </h3>
                <p className="font-body text-lg text-livelinx-purple200">{user.scores}</p>
              </div>
            )}

            {/* Additional Information */}
            <div className="space-y-3 border-t border-livelinx-lightgray100 pt-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-livelinx-grey200">
                  <Calendar className="h-4 w-4" />
                  {t('stats.startDate')}
                </span>
                <span className="font-body text-sm text-livelinx-purple200">
                  {user.startDate
                    ? new Date(user.startDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : '--'}
                </span>
              </div>
              {user.nps !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-livelinx-grey200">{t('stats.nps')}</span>
                  <span className="font-body text-sm text-livelinx-purple200">{user.nps}%</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-livelinx-grey200">{t('stats.hasAccessed')}</span>
                <Badge variant={user.hasAccessed ? 'default' : 'outline'} className="text-xs">
                  {user.hasAccessed ? t('stats.yes') : t('stats.no')}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-livelinx-grey200">{t('stats.hasCompleted')}</span>
                <Badge variant={user.hasCompleted ? 'default' : 'outline'} className="text-xs">
                  {user.hasCompleted ? t('stats.yes') : t('stats.no')}
                </Badge>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
